// ============================================================================
// tokenStorage — SINGLE source of truth for auth-token persistence
// ============================================================================
//
// SECURITY (sec/s0f-token-storage):
// Access/refresh tokens were previously read/written via `localStorage.*`
// inline in several places (lib/api.ts + stores/authStore.ts via the zustand
// `persist` default storage). That scatters the storage backend across the
// codebase and makes the XSS-exposure surface hard to reason about or change.
//
// This module centralizes EVERY token read/write/clear behind one swappable
// backend. Today the backend is `localStorage` (chosen deliberately — see
// "Why localStorage, not sessionStorage" below). When the backend gains
// httpOnly-cookie support, only THIS file changes.
//
// ----------------------------------------------------------------------------
// Why localStorage, not sessionStorage (conservative, non-breaking choice)
// ----------------------------------------------------------------------------
// sessionStorage is cleared on tab close → smaller XSS window, BUT it breaks:
//   1. "Stay logged in" across browser restarts.
//   2. Silent refresh-token rotation surviving a full page reload
//      (zustand `persist` + `onRehydrateStorage` rely on durable storage).
//   3. Multi-tab continuity for newly-opened tabs.
// Per task guidance, when sessionStorage would break remember-me / refresh
// across reloads we keep localStorage and centralize instead. So we do.
//
// ----------------------------------------------------------------------------
// TODO (httpOnly-cookie migration — requires BACKEND work, out of scope here)
// ----------------------------------------------------------------------------
// The ONLY robust fix for XSS token theft is to stop exposing tokens to JS:
//   * Backend issues access + refresh tokens as `Secure; HttpOnly; SameSite`
//     cookies on /auth/login + /auth/refresh.
//   * Axios switches to `withCredentials: true` and STOPS attaching the
//     Authorization header from JS.
//   * This module's read paths return null (tokens no longer in JS) and the
//     interceptor logic in lib/api.ts is adjusted accordingly.
// Until the backend ships httpOnly cookies, this centralized localStorage
// backend is the lowest-risk hardening step.
// ============================================================================

export const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  AUTH_PERSIST: 'auth-storage', // zustand persist key (also holds token mirror)
} as const

// ----------------------------------------------------------------------------
// Backend abstraction — the single swap point.
// `globalThis.localStorage` is read lazily so SSR / non-browser / private-mode
// contexts (where access can throw) degrade gracefully instead of crashing.
// ----------------------------------------------------------------------------
interface KeyValueBackend {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function getBackend(): KeyValueBackend | null {
  try {
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
      return globalThis.localStorage
    }
  } catch {
    // Accessing localStorage can throw (e.g. iOS Safari private mode, sandboxed
    // iframes). Treat as "no storage available" rather than failing hard.
  }
  return null
}

// ----------------------------------------------------------------------------
// Public API — all callers go through these. Each operation is fail-safe.
// ----------------------------------------------------------------------------

export function getAccessToken(): string | null {
  return getBackend()?.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN) ?? null
}

export function getRefreshToken(): string | null {
  return getBackend()?.getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN) ?? null
}

export function setTokens(
  accessToken: string,
  refreshToken: string | null,
): void {
  const backend = getBackend()
  if (!backend) return
  try {
    backend.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, accessToken)
    if (refreshToken) {
      backend.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
    }
  } catch {
    /* storage quota / disabled — non-fatal */
  }
}

export function clearAllAuth(): void {
  const backend = getBackend()
  if (!backend) return
  try {
    backend.removeItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN)
    backend.removeItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN)
    backend.removeItem(TOKEN_STORAGE_KEYS.USER)
    // Also clear the zustand persist blob so /login does not see stale user
    // state and bounce back to /dashboard (PART_4_LOOP_FIX behaviour).
    backend.removeItem(TOKEN_STORAGE_KEYS.AUTH_PERSIST)
  } catch {
    /* non-fatal */
  }
}

// ----------------------------------------------------------------------------
// zustand `persist` adapter — lets the auth store route its persistence through
// this same backend (createJSONStorage shape). Keeps the storage backend in ONE
// place. Falls back to a no-op store when no backend is available.
// ----------------------------------------------------------------------------
export function getPersistStorage(): KeyValueBackend {
  return {
    getItem: (key) => getBackend()?.getItem(key) ?? null,
    setItem: (key, value) => {
      try {
        getBackend()?.setItem(key, value)
      } catch {
        /* non-fatal */
      }
    },
    removeItem: (key) => {
      try {
        getBackend()?.removeItem(key)
      } catch {
        /* non-fatal */
      }
    },
  }
}

// ----------------------------------------------------------------------------
// Cookie hardening (S0-F, per Master decision)
// ----------------------------------------------------------------------------
// The current backend is localStorage, which has NO cookie attributes. The
// robust fix (httpOnly cookies) requires BACKEND coordination — the server must
// Set-Cookie the access/refresh tokens on /auth/login + /auth/refresh (owned by
// the backend owner). Until that lands, this app must never set a token cookie
// without the mandatory security attributes. These constants/helpers centralize
// cookie writes the same way the localStorage backend is centralized, so no
// call site can omit `Secure` or `SameSite=Strict`.
//
// NOTE: a cookie written from JS can NEVER be HttpOnly (that flag is server-only
// by design) — which is exactly why the strongest fix must be done backend-side.
export const SECURE_COOKIE_ATTRIBUTES = 'Secure; SameSite=Strict; Path=/' as const

/**
 * Serialize a `document.cookie` string with the mandatory security attributes
 * baked in (`Secure; SameSite=Strict; Path=/`). Use this for ANY cookie write
 * so a token-bearing cookie can never be created without them.
 */
export function buildSecureCookie(
  name: string,
  value: string,
  maxAgeSeconds?: number,
): string {
  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    SECURE_COOKIE_ATTRIBUTES,
  ]
  if (typeof maxAgeSeconds === 'number') {
    parts.push(`Max-Age=${Math.floor(maxAgeSeconds)}`)
  }
  return parts.join('; ')
}
